import { useState, useEffect, useRef } from 'react'
import { storiesApi, type Story } from '../../api/stories'
import { mediaApi } from '../../api/media'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'
import { StoryViewer } from './StoryViewer'
import { AddStoryModal } from './AddStoryModal'

interface Highlight {
  id: string
  name: string
  cover_story_id?: string
}

interface StoryGroup {
  userId: string
  name: string
  avatarUrl?: string
  stories: Story[]
}

export function StoryFeed() {
  const [groups, setGroups] = useState<StoryGroup[]>([])
  const [viewerIdx, setViewerIdx] = useState<number | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [highlights, setHighlights] = useState<Highlight[]>([])
  const [highlightGroups, setHighlightGroups] = useState<StoryGroup[]>([])
  const [highlightViewerIdx, setHighlightViewerIdx] = useState<number | null>(null)
  const [newHighlightName, setNewHighlightName] = useState('')
  const [showCreateHighlight, setShowCreateHighlight] = useState(false)
  const [editingHighlight, setEditingHighlight] = useState<string | null>(null)
  const [editHighlightName, setEditHighlightName] = useState('')
  const { userId } = useAuthStore()
  const chats = useChatStore((s) => s.chats)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Collect peer user IDs from private chats + self
    const peerIds = new Set<string>()
    if (userId) peerIds.add(userId)
    for (const chat of chats) {
      if (chat.type === 'private' && chat.peer?.id) {
        peerIds.add(chat.peer.id)
      }
    }
    if (!peerIds.size) return

    storiesApi.getFeed(Array.from(peerIds))
      .then(({ stories }) => {
        // Group stories by owner
        const byOwner = new Map<string, Story[]>()
        for (const s of stories) {
          const arr = byOwner.get(s.owner_id) ?? []
          arr.push(s)
          byOwner.set(s.owner_id, arr)
        }

        const result: StoryGroup[] = []
        // Own stories first
        if (userId && byOwner.has(userId)) {
          result.push({
            userId,
            name: 'My Story',
            stories: byOwner.get(userId)!,
          })
        }
        // Others
        for (const [uid, sts] of byOwner.entries()) {
          if (uid === userId) continue
          const chat = chats.find((c) => c.type === 'private' && c.peer?.id === uid)
          const peer = chat?.peer
          result.push({
            userId: uid,
            name: peer ? [peer.first_name, peer.last_name].filter(Boolean).join(' ') || peer.username || uid : uid,
            avatarUrl: peer?.avatar_url ? mediaApi.getURL(peer.avatar_url) : undefined,
            stories: sts,
          })
        }
        setGroups(result)
      })
      .catch(() => {})
  }, [chats, userId])

  useEffect(() => {
    if (!userId) return
    storiesApi.listHighlights(userId)
      .then((r) => setHighlights(r.highlights ?? []))
      .catch(() => {})
  }, [userId])

  const openHighlight = async (highlight: Highlight, _idx: number) => {
    try {
      const { stories } = await storiesApi.getHighlightStories(highlight.id)
      if (!stories.length) return
      setHighlightGroups([{ userId: userId!, name: highlight.name, stories }])
      setHighlightViewerIdx(0)
    } catch { /* ignore */ }
  }

  const createHighlight = async () => {
    const name = newHighlightName.trim()
    if (!name) return
    try {
      const h = await storiesApi.createHighlight(name)
      setHighlights((prev) => [...prev, h])
      setNewHighlightName('')
      setShowCreateHighlight(false)
    } catch { /* ignore */ }
  }

  const deleteHighlight = async (id: string) => {
    try {
      await storiesApi.deleteHighlight(id)
      setHighlights((prev) => prev.filter((h) => h.id !== id))
    } catch { /* ignore */ }
  }

  const renameHighlight = async (id: string) => {
    const name = editHighlightName.trim()
    if (!name) { setEditingHighlight(null); return }
    try {
      await storiesApi.updateHighlight(id, name)
      setHighlights((prev) => prev.map((h) => h.id === id ? { ...h, name } : h))
    } catch { /* ignore */ }
    setEditingHighlight(null)
  }

  if (groups.length === 0 && !userId) return null

  return (
    <>
      <div className="border-b border-divider shrink-0">
        <div ref={scrollRef} className="flex gap-2 px-3 py-2.5 overflow-x-auto scrollbar-thin">
          {/* Add story button (always first) */}
          <button
            onClick={() => setShowAdd(true)}
            className="flex flex-col items-center gap-1 shrink-0 group"
          >
            <div className="relative w-12 h-12 rounded-full bg-bg-input flex items-center justify-center
                            border-2 border-dashed border-divider group-hover:border-accent transition-colors">
              <span className="text-accent text-xl leading-none">+</span>
            </div>
            <span className="text-txt-meta text-[10px] w-12 text-center truncate">Add</span>
          </button>

          {/* Story rings */}
          {groups.map((g, i) => (
            <StoryRing
              key={g.userId}
              group={g}
              onClick={() => setViewerIdx(i)}
            />
          ))}
        </div>
      </div>

      {/* Highlights row — показываем только если есть хотя бы один хайлайт */}
      {highlights.length > 0 && (
        <div className="flex gap-2 px-3 pb-2 overflow-x-auto scrollbar-thin border-b border-divider">
          {/* Add highlight button */}
          {!showCreateHighlight ? (
            <button
              onClick={() => setShowCreateHighlight(true)}
              className="flex flex-col items-center gap-1 shrink-0"
            >
              <div className="w-12 h-12 rounded-full bg-bg-input border-2 border-dashed border-divider flex items-center justify-center text-txt-meta text-lg">
                +
              </div>
              <span className="text-txt-meta text-[10px] w-14 text-center truncate">New</span>
            </button>
          ) : (
            <div className="flex items-center gap-1 shrink-0">
              <input
                autoFocus
                value={newHighlightName}
                onChange={(e) => setNewHighlightName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createHighlight(); if (e.key === 'Escape') setShowCreateHighlight(false) }}
                placeholder="Название…"
                className="bg-bg-input text-txt-primary rounded-xl px-2 py-1 text-xs outline-none w-20 focus:ring-1 focus:ring-accent placeholder-txt-meta"
              />
              <button onClick={createHighlight} className="text-accent text-xs hover:underline">OK</button>
            </div>
          )}
          {highlights.map((h, i) => (
            <div key={h.id} className="flex flex-col items-center gap-1 shrink-0 relative group">
              <button
                onClick={() => openHighlight(h, i)}
                className="w-12 h-12 rounded-full bg-bg-input border-2 border-divider flex items-center justify-center text-txt-secondary text-lg hover:border-accent transition-colors"
              >
                📌
              </button>
              {editingHighlight === h.id ? (
                <input
                  autoFocus
                  value={editHighlightName}
                  onChange={(e) => setEditHighlightName(e.target.value)}
                  onBlur={() => renameHighlight(h.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') renameHighlight(h.id); if (e.key === 'Escape') setEditingHighlight(null) }}
                  className="w-14 text-[10px] text-center bg-bg-input text-txt-primary rounded px-1 outline-none ring-1 ring-accent"
                  maxLength={24}
                />
              ) : (
                <span
                  className="text-txt-meta text-[10px] w-12 text-center truncate cursor-pointer hover:text-txt-secondary"
                  onDoubleClick={() => { setEditingHighlight(h.id); setEditHighlightName(h.name) }}
                  title="Двойной клик для переименования"
                >{h.name}</span>
              )}
              <button
                onClick={() => deleteHighlight(h.id)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] opacity-0 group-hover:opacity-100 transition-opacity"
                title="Удалить"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {viewerIdx !== null && (
        <StoryViewer
          groups={groups}
          initialGroupIdx={viewerIdx}
          onClose={() => setViewerIdx(null)}
          highlights={highlights}
        />
      )}

      {highlightViewerIdx !== null && highlightGroups.length > 0 && (
        <StoryViewer
          groups={highlightGroups}
          initialGroupIdx={0}
          onClose={() => { setHighlightViewerIdx(null); setHighlightGroups([]) }}
        />
      )}

      {showAdd && (
        <AddStoryModal
          onClose={() => setShowAdd(false)}
          onCreated={(story) => {
            setGroups((prev) => {
              const selfIdx = prev.findIndex((g) => g.userId === userId)
              if (selfIdx >= 0) {
                const updated = [...prev]
                updated[selfIdx] = { ...updated[selfIdx], stories: [...updated[selfIdx].stories, story] }
                return updated
              }
              return [{ userId: userId!, name: 'My Story', stories: [story] }, ...prev]
            })
          }}
        />
      )}
    </>
  )
}

function StoryRing({ group, onClick }: { group: StoryGroup; onClick: () => void }) {
  const { userId } = useAuthStore()
  const isOwn = group.userId === userId

  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1 shrink-0">
      <div className="relative w-12 h-12 rounded-full p-0.5 bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-400">
        <div className="w-full h-full rounded-full bg-bg-primary overflow-hidden p-0.5">
          {group.avatarUrl ? (
            <img src={group.avatarUrl} alt={group.name} className="w-full h-full rounded-full object-cover" />
          ) : (
            <div className="w-full h-full rounded-full bg-accent/20 flex items-center justify-center text-accent text-sm font-bold">
              {group.name[0]?.toUpperCase()}
            </div>
          )}
        </div>
      </div>
      <span className="text-txt-secondary text-[10px] w-12 text-center truncate">
        {isOwn ? 'My Story' : group.name.split(' ')[0]}
      </span>
    </button>
  )
}
